import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import { Icon } from "@iconify/react";
import moment from "moment";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import { getDataWithComp } from "../../../utils/SamuiUtils";
import { formatDateMonthYear } from "../../../utils/DateUtils";
import { Link } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  CircleMarker,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "../../../utils/fix-leaflet-icon";

export default function JobServiceClosePage() {
  const { id } = useParams(); // Calendar_Id หรือ Round_Id
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapType, setMapType] = useState("arrive"); // "arrive" or "leave"
  const [checkInLocation, setCheckInLocation] = useState({ lat: null, lng: null });
  const [checkOutLocation, setCheckOutLocation] = useState({ lat: null, lng: null });
  const [checkInTimestamp, setCheckInTimestamp] = useState(null); // DD/MM/YYYY HH:mm:ss จาก check-in
  const [checkOutTimestamp, setCheckOutTimestamp] = useState(null); // DD/MM/YYYY HH:mm:ss จาก check-out
  const [editingServiceId, setEditingServiceId] = useState(null); // ID ของ service ที่กำลังแก้ไข
  const [editedSteps, setEditedSteps] = useState({}); // เก็บข้อมูลที่แก้ไข { stepId: { qty, usedQty, unit, remark, memo, checked } }
  const [serviceMemos, setServiceMemos] = useState({}); // เก็บ memo ของแต่ละ service { serviceId: "memo text" }
  const [editingMemoServiceId, setEditingMemoServiceId] = useState(null); // service ที่กำลังแก้ memo แบบ inline
  const [serviceImages, setServiceImages] = useState({}); // เก็บรูปภาพของแต่ละ service { serviceId: [imageUrls] }
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedServiceImages, setSelectedServiceImages] = useState([]);
  const [imageModalTitle, setImageModalTitle] = useState("บันทึกการบริการ");
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [itemList, setItemList] = useState([]);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const ensureServicePdfExists = async () => {
    const docId = data?.id || null;
    if (!docId) return null;

    const checkRes = await api.get("/app/get-service-pdf", { params: { doc_id: docId } });
    if (checkRes.data?.file_url) return checkRes.data.file_url;

    const services = data?.services || [];
    const allImgUrls = services.flatMap((srv) => serviceImages[srv.id] || []);
    if (!allImgUrls.length) {
      throw new Error("ไม่พบรูปบริการสำหรับสร้าง PDF");
    }

    const loadImg = (url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve(c.toDataURL("image/jpeg", 0.9));
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });

    const allLoaded = await Promise.all(allImgUrls.map(loadImg));
    const hasValidImage = allLoaded.some(Boolean);
    if (!hasValidImage) {
      throw new Error("ไม่สามารถโหลดรูปบริการเพื่อสร้าง PDF ได้");
    }

    const { jsPDF } = await import("jspdf");
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = 210;
    const pageH = 297;
    const margin = 10;
    const usableW = pageW - margin * 2;
    const DPI = 300;
    const MM_TO_PX = DPI / 25.4;
    const gap = 3;
    const colW = (usableW - gap) / 2;
    const rowH = colW * 0.75;
    const makeTextImg = (text, fontSizePt, bold = false) => {
      const fontSizePx = fontSizePt * (DPI / 72);
      const w = Math.round(usableW * MM_TO_PX);
      const h = Math.round(fontSizePx * 2);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "#111111";
      ctx.font = `${bold ? "600 " : ""}${fontSizePx}px Sarabun, "Noto Sans Thai", Kanit, Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, w / 2, h / 2);
      return { dataUrl: canvas.toDataURL("image/png"), hMM: h / MM_TO_PX };
    };

    const locationName = data?.serviceAddrName && data.serviceAddrName !== "-" ? data.serviceAddrName : "";
    const fullAddr = data?.customer?.address && data.customer.address !== "-" ? data.customer.address : "";
    const locationText = [locationName, fullAddr].filter(Boolean).join(" ") || "-";
    const rawDate = data?.schedule?.date;
    const mDate = rawDate ? moment(rawDate) : null;
    const dateStr = mDate && mDate.isValid()
      ? `${mDate.format("DD/MM/")}${mDate.year() + 543}`
      : (rawDate || "-");
    const serviceNames = services
      .filter((srv) => (serviceImages[srv.id] || []).length > 0)
      .map((srv) => srv.title || "-")
      .join(", ");

    let y = margin;
    const titleImg = makeTextImg(`Picture Service: ${serviceNames}`, 16, true);
    pdf.addImage(titleImg.dataUrl, "PNG", margin, y, usableW, titleImg.hMM);
    y += titleImg.hMM + 2;
    const locImg = makeTextImg(locationText, 11);
    pdf.addImage(locImg.dataUrl, "PNG", margin, y, usableW, locImg.hMM);
    y += locImg.hMM + 1;
    const subImg = makeTextImg(`วันที่เข้าบริการ: ${dateStr}`, 11);
    pdf.addImage(subImg.dataUrl, "PNG", margin, y, usableW, subImg.hMM);
    y += subImg.hMM + 4;

    allLoaded.forEach((img, i) => {
      const col = i % 2;
      if (col === 0 && y + rowH > pageH - margin) {
        pdf.addPage();
        y = margin;
      }
      if (img) {
        pdf.addImage(img, "JPEG", margin + col * (colW + gap), y, colW, rowH);
      }
      if (col === 1 || i === allLoaded.length - 1) y += rowH + gap;
    });

    const base64 = pdf.output("datauristring").split(",")[1];
    const uploadRes = await api.post("/app/upload-pdf-report", {
      pdf_base64: base64,
      filename: `service_pictures_${docId}_${Date.now()}.pdf`,
      doc_id: docId,
      upload_by: window.localStorage.getItem("name") || "System",
    });

    if (uploadRes.data?.status !== "OK") {
      throw new Error(uploadRes.data?.error || "อัปโหลด PDF ไม่สำเร็จ");
    }
    return uploadRes.data.file_url || null;
  };

  // โหลดข้อมูลตาม id
  useEffect(() => {
    fetchJobData();
  }, [id]);

  useEffect(() => {
    const fetchGroupsAndItems = async () => {
      try {
        const [groups, items] = await Promise.all([
          getDataWithComp("Tb_Set_ItmGroup", "AND IsShowInWork = 1 AND Group_Status = 'Y'", { fields: ["Group_Id", "Group_Name"] }),
          getDataWithComp("Tb_Set_Item", "AND Item_Status = 'Y' ORDER BY Item_Short_Name, Item_Name", { fields: ["Item_Id", "Item_Code", "Item_Name", "Item_Unit_ST", "Item_Group"] }),
        ]);
        const allGroups = Array.isArray(groups) ? new Set(groups.map((g) => String(g.Group_Id))) : null;
        const allItems = Array.isArray(items) ? items : [];
        setItemList(allGroups ? allItems.filter((i) => allGroups.has(String(i.Item_Group))) : allItems);
      } catch (e) {
        console.error(e);
      }
    };
    fetchGroupsAndItems();
  }, []);

  // โหลดข้อมูลจาก Mobile (get-job-close) หรือ mock เมื่อมี services
  useEffect(() => {
    if (data?.services && data.services.length > 0) {
      // ถ้ามี closeData จาก Mobile ให้ใช้ข้อมูลนั้น
      if (data.closeData) {
        setServiceMemos((prev) => ({
          ...(data.closeData.serviceMemos || {}),
          ...prev,
        }));
        setServiceImages((prev) => ({
          ...(data.closeData.serviceImages || {}),
          ...prev,
        }));
        return;
      }
      // Mock data เมื่อยังไม่มีข้อมูลจาก Mobile
      const mockServiceMemos = {};
      data.services.forEach((srv, idx) => {
        if (idx === 0) {
          mockServiceMemos[srv.id] = "บันทึกการบริการ: ตรวจสอบระบบครบถ้วนแล้ว ไม่พบปัญหาใดๆ ควรติดตามผลอีกครั้งในครั้งต่อไป";
        } else if (idx === 1) {
          mockServiceMemos[srv.id] = "ทำความสะอาดเครื่องปรับอากาศเรียบร้อยแล้ว ตรวจสอบการทำงานปกติ";
        }
      });
      setServiceMemos(prev => (Object.keys(prev).length > 0 ? prev : mockServiceMemos));
      const mockServiceImages = {};
      data.services.forEach((srv, idx) => {
        const imageCount = 5 + (idx % 4);
        mockServiceImages[srv.id] = Array.from({ length: imageCount }, (_, i) =>
          `https://picsum.photos/400/300?random=${idx * 10 + i}`
        );
      });
      setServiceImages(prev => (Object.keys(prev).length > 0 ? prev : mockServiceImages));
    }
  }, [data?.services, data?.closeData]);

  const fetchJobData = async () => {
    try {
      setLoading(true);
      setServiceMemos({});
      setServiceImages({});
      setCheckInLocation({ lat: null, lng: null });
      setCheckOutLocation({ lat: null, lng: null });
      setCheckInTimestamp(null);
      setCheckOutTimestamp(null);

      // ดึงข้อมูล Calendar (ถ้า id เป็น Calendar_Id)
      let calendarData = null;
      let contractId = null;
      let roundId = null;
      let roundNo = null;
      let roundDate = null;
      let teamId = null;
      let teamName = null;
      let jobDate = null;
      let timeStart = null;
      let timeEnd = null;
      let jobStatus = null;

      // ลองดึงข้อมูลด้วย Calendar_Id ก่อน (งานที่ assign แล้ว)
      const compIdForFetch = localStorage.getItem("company");
      try {
        const calendarRes = await api.get(`/get-assigned-jobs`, {
          params: { calendar_id: id, ...(compIdForFetch ? { comp_id: compIdForFetch } : {}) }
        });
        
        if (calendarRes.data?.status === "OK" && calendarRes.data.result?.length > 0) {
          calendarData = calendarRes.data.result[0];
          contractId = calendarData.JobId;
          roundId = calendarData.Round_Id;
          roundNo = calendarData.Round_No;
          roundDate = calendarData.Round_Date;
          teamId = calendarData.teamId;
          teamName = calendarData.teamName;
          // ดึง Time_Start และ Time_End
          timeStart = calendarData.Time_Start;
          timeEnd = calendarData.Time_End;
          // ดึง Job_Date หรือ start
          if (calendarData.Job_Date) {
            jobDate = moment(calendarData.Job_Date).format("YYYY-MM-DD");
          } else if (calendarData.start) {
            const startDate = new Date(calendarData.start);
            jobDate = startDate.toISOString().split('T')[0];
          }
          jobStatus = calendarData.jobStatus || "รอปิดงาน";
        } else {
          console.log("No calendar data found with calendar_id:", id);
        }
      } catch (err) {
        console.log("Error fetching with calendar_id, trying other methods...", err);
      }

      // ถ้าไม่เจอ Calendar_Id ลองดึงจาก Round_Id
      if (!contractId) {
        try {
          const roundRes = await api.get(`/get-pending-jobs`, {
            params: { round_id: id }
          });
          
          if (roundRes.data?.status === "OK" && roundRes.data.result?.length > 0) {
            const roundData = roundRes.data.result[0];
            contractId = roundData.JobId;
            roundId = roundData.Round_Id;
            roundNo = roundData.Round_No;
            roundDate = roundData.Round_Date;
            jobStatus = "รอปิดงาน";
          }
        } catch (err) {
          console.log("Not a Round_Id, trying as Contract_Id...", err);
        }
      }

      // ถ้ายังไม่เจอ ลองใช้ id เป็น Contract_Id โดยตรง
      if (!contractId) {
        try {
          // ลองดึงข้อมูลสัญญาด้วย id โดยตรง (อาจเป็น Contract_Id)
          const testContractRes = await api.get(`/get-contract/${id}`);
          if (testContractRes.data.status === "OK") {
            contractId = id; // ใช้ id เป็น Contract_Id
            jobStatus = "รอปิดงาน";
            console.log("Using id as Contract_Id:", id);
          }
        } catch (err) {
          console.log("Not a Contract_Id either", err);
        }
      }

      if (!contractId) {
        throw new Error("ไม่พบข้อมูลงาน กรุณาตรวจสอบ ID ที่ส่งมา");
      }

      // ดึงข้อมูลสัญญา
      const contractRes = await api.get(`/get-contract/${contractId}`);
      if (contractRes.data.status !== "OK") {
        throw new Error(contractRes.data.message || "ไม่สามารถดึงข้อมูลสัญญาได้");
      }

      const contractData = contractRes.data;
      const header = contractData.header || {};
      
      // ดึงข้อมูลสถานที่บริการเพิ่มเติมจาก API
      let serviceAddrInfo = {};
      if (header.ServiceAddr_Id) {
        try {
          const serviceAddrRes = await api.get(`/get-tb-set-service-addr-by-id/${header.ServiceAddr_Id}`);
          if (serviceAddrRes.data.status === "OK" && serviceAddrRes.data.result?.recordset?.[0]) {
            serviceAddrInfo = serviceAddrRes.data.result.recordset[0];
          }
        } catch (err) {
          console.error("Error fetching service address info:", err);
        }
      }
      
      // หา Round ที่ต้องการ
      let selectedRound = null;
      if (roundId && contractData.works) {
        selectedRound = contractData.works.find(w => w.Round_Id === roundId);
      } else if (roundNo && contractData.works) {
        selectedRound = contractData.works.find(w => w.Round_No === roundNo);
      }

      if (!selectedRound && contractData.works?.length > 0) {
        selectedRound = contractData.works[0]; // ใช้ round แรกถ้าไม่เจอ
      }

      // สร้าง index item name ของ step จากข้อมูลสัญญา เพื่อใช้ fallback
      const contractStepItemMap = new Map();
      (selectedRound?.Services || []).forEach((srv) => {
        (srv?.Steps || []).forEach((step) => {
          const byId = step?.Step_Id != null ? String(step.Step_Id) : "";
          const byTitle = `${srv?.Service_Name || ""}__${step?.Step_Title || ""}`.trim();
          const itemName = step?.Item_Name || step?.Step_Item_Name || "";
          if (itemName) {
            if (byId) contractStepItemMap.set(`id:${byId}`, itemName);
            if (byTitle) contractStepItemMap.set(`title:${byTitle}`, itemName);
          }
        });
      });

      const getCurrentItemName = (step) =>
        step?.Item_Name ||
        step?.Step_Item_Name ||
        step?.Current_Item_Name ||
        step?.Used_Item_Name ||
        "";

      /** รหัสสินค้าจาก API (รองรับหลายชื่อฟิลด์) — ใช้ให้ <select> แสดงค่าที่เลือก */
      const normalizeStepItemId = (step) => {
        const raw =
          step?.Item_Id ??
          step?.item_id ??
          step?.Step_Item_Id ??
          step?.step_item_id;
        if (raw === null || raw === undefined || raw === "") return null;
        const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
        return Number.isFinite(n) && n > 0 ? n : null;
      };

      const getOriginalItemName = (step) =>
        step?.Original_Item_Name ||
        step?.Step_Item_Name_Original ||
        step?.Step_Item_Name_Prescribed ||
        step?.Prescribed_Item_Name ||
        step?.Default_Item_Name ||
        "";

      // สร้างชื่อลูกค้า
      const customerName = header.DisplayName 
        ? header.DisplayName
        : header.AR_Name 
        ? `${header.AR_Name}${header.AR_LastName ? ' ' + header.AR_LastName : ''}`.trim()
        : "-";

      // ดึงชื่อสถานที่ให้บริการ (ใช้จาก serviceAddrInfo ก่อน ถ้าไม่มีใช้จาก header)
      const serviceAddrName = serviceAddrInfo.ServiceAddr_Name || header.ServiceAddr_Name || header.Contract_ServiceAddr_Name || "-";

      // ดึงข้อมูลผู้ติดต่อและเบอร์โทร (ใช้จาก header ก่อน ถ้าไม่มีใช้จาก serviceAddrInfo)
      const contactName = header.Contract_ContactName || serviceAddrInfo.Advisor_Name || header.AR_ContactName || "-";
      const contactTel = header.Contract_ContactTel || serviceAddrInfo.Advisor_Tel || header.AR_Tel || "-";

      // สร้างที่อยู่ (ใช้ข้อมูลจาก serviceAddrInfo ก่อน ถ้าไม่มีใช้จาก header)
      const addressParts = [
        serviceAddrInfo.AddrNo || header.AddrNo,
        serviceAddrInfo.Building || null,
        serviceAddrInfo.Village || header.Village,
        serviceAddrInfo.Moo ? `หมู่ ${serviceAddrInfo.Moo}` : (header.Moo ? `หมู่ ${header.Moo}` : null),
        serviceAddrInfo.Soi ? `ซอย ${serviceAddrInfo.Soi}` : (header.Soi ? `ซอย ${header.Soi}` : null),
        serviceAddrInfo.Road ? `ถนน ${serviceAddrInfo.Road}` : (header.Road ? `ถนน ${header.Road}` : null),
        serviceAddrInfo.SubDistrictName || header.SubDistrict ? `แขวง/ตำบล ${serviceAddrInfo.SubDistrictName || header.SubDistrict}` : null,
        serviceAddrInfo.DistrictName || header.District ? `เขต/อำเภอ ${serviceAddrInfo.DistrictName || header.District}` : null,
        serviceAddrInfo.ProvinceName || header.Province || null,
        serviceAddrInfo.Zipcode || header.PostCode || null
      ].filter(Boolean);
      const address = addressParts.length > 0 ? addressParts.join(' ') : "-";

      // สร้าง location (ถ้ามี) - สำหรับเวลาเข้าและเวลาออก (ใช้ที่อยู่เดียวกันก่อน)
      const location = serviceAddrInfo.Latitude && serviceAddrInfo.Longitude
        ? { 
            lat: parseFloat(serviceAddrInfo.Latitude), 
            lng: parseFloat(serviceAddrInfo.Longitude),
            place: address
          }
        : header.ServiceAddr_Latitude && header.ServiceAddr_Longitude
        ? { 
            lat: parseFloat(header.ServiceAddr_Latitude), 
            lng: parseFloat(header.ServiceAddr_Longitude),
            place: address
          }
        : null;

      const compId = localStorage.getItem("company") || "1";

      // ดึงข้อมูล check-in/check-out (ก่อนเข้าบริการ / หลังบริการ) แยกตาม Check_Status
      let photosBefore = [];
      let photosAfter = [];
      let checkInLoc = { lat: null, lng: null };
      let checkOutLoc = { lat: null, lng: null };
      let checkInTs = null;
      let checkOutTs = null;
      if (contractId) {
        try {
          const checkinRes = await api.get("/get-job-checkin", {
            params: {
              comp_id: compId,
              job_id: contractId,
              ...(id ? { calendar_id: id } : {}),
            },
          });
          if (checkinRes.data?.status === "OK" && Array.isArray(checkinRes.data?.result)) {
            const checkIns = checkinRes.data.result.filter((r) => (r.Check_Status || "").toLowerCase() === "check_in");
            const checkOuts = checkinRes.data.result.filter((r) => (r.Check_Status || "").toLowerCase() === "check_out");
            // เลือก check_in ที่มีรูปก่อน (fallback สำหรับข้อมูลเก่าที่รูปอยู่กับ record อื่น)
            const checkInRecord = checkIns.find((r) => r.Images?.length > 0) || checkIns[0];
            const checkOutRecord = checkOuts.find((r) => r.Images?.length > 0) || checkOuts[0];
            if (checkInRecord) {
              photosBefore = (checkInRecord.Images || []).map((i) => i.File_Url);
              if (checkInRecord.Latitude != null && checkInRecord.Longitude != null) {
                checkInLoc = { lat: parseFloat(checkInRecord.Latitude), lng: parseFloat(checkInRecord.Longitude) };
              }
              checkInTs = checkInRecord.Created_Date_Formatted || null;
            }
            if (checkOutRecord) {
              photosAfter = (checkOutRecord.Images || []).map((i) => i.File_Url);
              if (checkOutRecord.Latitude != null && checkOutRecord.Longitude != null) {
                checkOutLoc = { lat: parseFloat(checkOutRecord.Latitude), lng: parseFloat(checkOutRecord.Longitude) };
              }
              checkOutTs = checkOutRecord.Created_Date_Formatted || null;
            }
          }
        } catch (err) {
          console.warn("get-job-checkin:", err?.response?.data || err.message);
        }
      }
      setCheckInLocation(checkInLoc);
      setCheckOutLocation(checkOutLoc);
      setCheckInTimestamp(checkInTs);
      setCheckOutTimestamp(checkOutTs);

      // ดึงข้อมูลปิดงาน - ใช้เส้น Web (get-job-close-web) รับ calendar_id เท่านั้น
      let closeData = null;
      try {
        const closeRes = await api.get("/get-job-close-web", {
          params: {
            calendar_id: id,
            ...(roundNo != null ? { round_no: roundNo } : {}),
            ...(compId ? { comp_id: compId } : {}),
            include_item_name: 1,
            include_step_item: 1,
          },
        });
        if (closeRes.data?.status === "OK" && closeRes.data?.result) {
          closeData = closeRes.data.result;
        }
      } catch (err) {
        console.warn("get-job-close-web:", err?.response?.data || err.message);
      }
      // Fallback: ใช้เส้นเดิม เมื่อ id อาจไม่ใช่ calendar_id
      if (!closeData && contractId && compId && (roundId || roundNo != null)) {
        try {
          const params = roundId ? { round_id: roundId } : { round_no: roundNo };
          const closeRes = await api.get("/get-job-close", {
            params: { comp_id: compId, job_id: contractId, ...params, include_item_name: 1, include_step_item: 1 },
          });
          if (closeRes.data?.status === "OK" && closeRes.data?.result) {
            closeData = closeRes.data.result;
          }
        } catch (err) {
          console.warn("get-job-close fallback:", err?.response?.data || err.message);
        }
      }

      // สร้าง serviceMemos และ serviceImages จาก closeData ทันที (ไม่ใช้ mock)
      const closeServiceMemos = {};
      const closeServiceImages = {};
      if (closeData?.Services?.length) {
        closeData.Services.forEach((s) => {
          closeServiceMemos[s.Service_Id] = s.Service_Memo || "";
          closeServiceImages[s.Service_Id] = (s.Images || []).map((i) => i.File_Url);
        });
      }

      // สร้าง services: ถ้ามี closeData ใช้จาก closeData โดยตรง (ข้อมูลบริการจาก Mobile)
      let services = [];
      if (closeData?.Services?.length) {
        services = closeData.Services.map((srv, idx) => {
          const steps = (srv.Steps || []).map((step, stepIdx) => {
            const stepIdKey = step.Step_Id != null ? `id:${String(step.Step_Id)}` : "";
            const stepTitleKey = `title:${srv.Service_Name || ""}__${step.Step_Title || ""}`.trim();
            const fallbackItemName = (stepIdKey && contractStepItemMap.get(stepIdKey))
              || contractStepItemMap.get(stepTitleKey)
              || "-";
            return {
              id: step.Step_Id || `step-${idx}-${stepIdx}`,
              name: step.Step_Title || "-",
              itemId: normalizeStepItemId(step),
              itemName: getCurrentItemName(step) || fallbackItemName,
              originalItemName: getOriginalItemName(step) || fallbackItemName,
              qty: step.Step_Qty_Prescribed ?? step.Step_Qty ?? 0,
              usedQty: step.Step_Qty ?? step.Step_Qty_Prescribed ?? 0,
              unit: step.Step_Unit || "-",
              remark: step.Step_Remark ?? "",
              overuseReasonName: step.Overuse_Reason_Name || null,
              checked: step.Step_Checked === 1 || step.Step_Checked === true
            };
          });
          return {
            id: srv.Service_Id || `srv-${idx}`,
            title: srv.Service_Name || "-",
            type: "ขั้นตอนการบริการ",
            steps,
            notDoneToday: srv.NotDoneToday ?? steps.some((st) => !st.checked)
          };
        });
      } else if (selectedRound?.Services?.length) {
        // Fallback: จาก contract เมื่อยังไม่มี closeData
        services = selectedRound.Services.map((srv, idx) => {
          const steps = (srv.Steps || []).map((step, stepIdx) => ({
            id: step.Step_Id || `step-${idx}-${stepIdx}`,
            name: step.Step_Title || "-",
            itemId: normalizeStepItemId(step),
            itemName: getCurrentItemName(step) || "-",
            originalItemName: getOriginalItemName(step) || step.Item_Name || step.Step_Item_Name || "-",
            qty: step.Step_Qty_Prescribed ?? step.Step_Qty ?? 0,
            usedQty: step.Step_Qty ?? step.Step_Qty_Prescribed ?? 0,
            unit: step.Step_Unit || "-",
            remark: step.Step_Remark ?? "",
            overuseReasonName: step.Overuse_Reason_Name || null,
            checked: step.Step_Checked === 1 || step.Step_Checked === true
          }));
          return {
            id: srv.Service_Id || `srv-${idx}`,
            title: srv.Service_Name || "-",
            type: "ขั้นตอนการบริการ",
            steps,
            notDoneToday: steps.some((st) => !st.checked)
          };
        });
      }

      // สร้าง title
      const title = selectedRound 
        ? `${customerName} บริการครั้งที่ ${selectedRound.Round_No || roundNo || '-'}`
        : `${customerName} บริการ`;

      // Format เวลา
      const formatTime = (time) => {
        if (!time) return null;
        if (typeof time === 'string' && time.includes(':')) {
          return time.substring(0, 5); // HH:mm
        }
        return moment(time).format("HH:mm");
      };

      setData({
        id: id,
        roundId: roundId || null,
        title: title,
        team: teamName || "-",
        status: jobStatus || "รอปิดงาน",
        schedule: {
          arrive: formatTime(timeStart) || "-",
          leave: formatTime(timeEnd) || "-",
          date: jobDate ? moment(jobDate).format("YYYY-MM-DD") : (roundDate || "-")
        },
        customer: {
          name: customerName,
          contractNo: header.Doc_No || "-",
          contractId: header.Doc_Id || null,
          address: address,
          province: header.Province || "-",
          contactName: contactName,
          contactTel: contactTel,
          quoteNo: header.Ref_DocNo || "-",
          quoteId: header.Ref_DocID || null
        },
        serviceAddrName: serviceAddrName,
        teamName: teamName || "-",
        location: location,
        photosBefore: photosBefore,
        photosAfter: photosAfter.length > 0
          ? photosAfter
          : (closeData?.Services?.flatMap(s => (s.Images || []).map(i => i.File_Url)) || []),
        services: services,
        customerSignature: closeData ? {
          signed: !!closeData.HasSignature,
          signatureImage: closeData.SignatureBase64 && closeData.SignatureMime
            ? `data:${closeData.SignatureMime};base64,${closeData.SignatureBase64}`
            : null,
          reason: closeData.Round_Sign_Remark || null,
          signedBy: closeData.Round_Sign_By,
          signedDate: closeData.Round_Sign_Date
        } : {
          signed: false,
          signatureImage: null,
          reason: null
        },
        closeData: closeData ? {
          serviceMemos: closeServiceMemos,
          serviceImages: closeServiceImages,
        } : null
      });

      // ตั้งค่า memo และรูปจาก closeData ทันที (ป้องกัน mock override)
      if (closeData?.Services?.length) {
        setServiceMemos(closeServiceMemos);
        setServiceImages(closeServiceImages);
      }

    } catch (error) {
      console.error("Error fetching job data:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.message || "ไม่สามารถดึงข้อมูลได้"
      });
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // ฟังก์ชันคำนวณระยะทาง (km)
  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 6371; // km
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // จุดที่เลือกแสดงตาม mapType (arrive=check_in, leave=check_out)
  const activeCheckLocation = mapType === "arrive" ? checkInLocation : checkOutLocation;

  // คำนวณระยะทางระหว่างสถานที่บริการกับจุด check-in/out ที่เลือก
  const distance = useMemo(() => {
    if (!data?.location || !activeCheckLocation.lat || !activeCheckLocation.lng) return null;
    return getDistanceKm(
      data.location.lat,
      data.location.lng,
      activeCheckLocation.lat,
      activeCheckLocation.lng
    )?.toFixed(2);
  }, [data?.location, activeCheckLocation]);

  const isJobClosed = data?.status === "สำเร็จ";

  // ฟังก์ชันสำหรับเริ่มแก้ไข service
  const handleEditService = (serviceId) => {
    if (isJobClosed) return;
    setEditingServiceId(serviceId);
    // เริ่มต้น editedSteps จากข้อมูลปัจจุบัน
    const service = data.services.find(s => s.id === serviceId);
    if (service) {
      const initialSteps = {};
      service.steps.forEach(step => {
        initialSteps[step.id] = {
          qty: step.qty,
          usedQty: step.usedQty ?? step.qty,
          unit: step.unit,
          remark: step.remark,
          memo: "", // memo เริ่มต้นเป็นค่าว่าง
          checked: step.checked || false,
          itemId: step.itemId ?? null,
          itemCode: step.itemCode || "",
          itemName: step.itemName || "",
        };
      });
      setEditedSteps(initialSteps);
    }
  };

  // ฟังก์ชันสำหรับอัพเดทข้อมูลที่แก้ไข
  const handleStepChange = (stepId, field, value) => {
    setEditedSteps(prev => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [field]: value
      }
    }));
  };

  // ฟังก์ชันสำหรับบันทึกข้อมูล
  const handleSaveService = async (serviceId) => {
    try {
      const service = data.services.find(s => s.id === serviceId);
      if (!service) return;

      // เตรียมข้อมูลสำหรับส่งไป API
      const stepsToUpdate = service.steps.map(step => {
        const edited = editedSteps[step.id] || {};
        return {
          Step_Id: step.id,
          Step_Qty: edited.usedQty !== undefined ? edited.usedQty : (step.usedQty ?? step.qty),
          Step_Unit: edited.unit !== undefined ? edited.unit : step.unit,
          Step_Remark: edited.remark !== undefined ? edited.remark : step.remark,
          Step_Checked: edited.checked !== undefined ? (edited.checked ? 1 : 0) : (step.checked ? 1 : 0),
          Item_Id: edited.itemId !== undefined ? edited.itemId : step.itemId,
          Item_Code: edited.itemCode !== undefined ? edited.itemCode : (step.itemCode || null),
          Item_Name: edited.itemName !== undefined ? edited.itemName : step.itemName,
        };
      });

      // เรียก API เพื่อ update steps
      // อัพเดททีละ step
      for (const step of stepsToUpdate) {
        await api.put(`/update-work-step/${step.Step_Id}`, {
          Step_Qty: step.Step_Qty,
          Step_Unit: step.Step_Unit,
          Step_Remark: step.Step_Remark,
          Step_Checked: step.Step_Checked,
          Item_Id: step.Item_Id,
          Item_Code: step.Item_Code,
          Item_Name: step.Item_Name,
        });
      }

      // บันทึก memo ระดับบริการ
      await api.put(`/update-work-service/${serviceId}`, {
        Service_Memo: serviceMemos[serviceId] || "",
      });
      
      Swal.fire({
        icon: "success",
        title: "บันทึกสำเร็จ",
        text: "บันทึกข้อมูลขั้นตอนการบริการสำเร็จ",
        confirmButtonText: "ตกลง"
      });

      // อัพเดทข้อมูลใน state
      setData(prev => ({
        ...prev,
        services: prev.services.map(s => {
          if (s.id === serviceId) {
            return {
              ...s,
              steps: s.steps.map(step => {
                const edited = editedSteps[step.id] || {};
                return {
                  ...step,
                  usedQty: edited.usedQty !== undefined ? edited.usedQty : (step.usedQty ?? step.qty),
                  unit: edited.unit !== undefined ? edited.unit : step.unit,
                  remark: edited.remark !== undefined ? edited.remark : step.remark,
                  checked: edited.checked !== undefined ? edited.checked : step.checked,
                  itemId: edited.itemId !== undefined ? edited.itemId : step.itemId,
                  itemCode: edited.itemCode !== undefined ? edited.itemCode : step.itemCode,
                  itemName: edited.itemName !== undefined ? edited.itemName : step.itemName,
                };
              })
            };
          }
          return s;
        })
      }));

      // ปิดโหมดแก้ไข
      setEditingServiceId(null);
      setEditedSteps({});
    } catch (error) {
      console.error("Error saving service:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.message || "ไม่สามารถบันทึกข้อมูลได้"
      });
    }
  };

  // ฟังก์ชันสำหรับยกเลิกการแก้ไข
  const handleCancelEdit = () => {
    setEditingServiceId(null);
    setEditedSteps({});
  };

  // ปุ่มปิดงาน - ส่ง action success ให้งาน (Job_Status = สำเร็จ)
  const handleCloseJob = async () => {
    if (isJobClosed) return;

    const jobId = data?.customer?.contractId;
    const calendarId = data?.id;

    if (!jobId) {
      Swal.fire({
        icon: "error",
        title: "ไม่พบข้อมูลงาน",
        text: "กรุณาโหลดหน้าใหม่",
      });
      return;
    }

    // ✅ เพิ่ม confirm ตรงนี้
    const confirm = await Swal.fire({
      title: "ยืนยันการปิดงาน?",
      text: "คุณต้องการปิดงานนี้ใช่หรือไม่",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ใช่, ปิดงาน",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    });

    // ❌ ถ้าไม่กดยืนยัน → หยุดเลย
    if (!confirm.isConfirmed) return;

    try {
      const res = await api.post("/app/job-success", {
        job_id: jobId,
        calendar_id: calendarId || undefined,
        ...(data?.roundId ? { round_id: data.roundId } : {}),
      });

      if (res.data?.status === "OK") {
        setData((prev) => (prev ? { ...prev, status: "สำเร็จ" } : prev));

      } else {
        throw new Error(res.data?.error || res.data?.message || "บันทึกไม่สำเร็จ");
      }

    } catch (err) {
      console.error("handleCloseJob:", err);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: err.response?.data?.error || err.message || "ไม่สามารถปิดงานได้",
      });
    }
  };

  if (loading) return <p className="p-6 text-gray-500">กำลังโหลดข้อมูล...</p>;
  if (!data) return <p className="p-6 text-red-500">ไม่พบข้อมูลงาน</p>;

  const renderMapModal = () => {
    if (!showMapModal || !data?.location) return null;
    return (
      <div key="map-modal">
        <style>{`
          .map-modal-overlay {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            background: rgba(0, 0, 0, 0.5) !important;
            z-index: 9999 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 1rem !important;
          }
          .map-modal-content {
            background: white !important;
            border-radius: 0.5rem !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
            width: 100% !important;
            max-width: 56rem !important;
            max-height: 90vh !important;
            overflow: auto !important;
            position: relative !important;
            z-index: 10000 !important;
          }
          .map-modal-content .leaflet-container {
            background: #fff !important;
            background-color: #fff !important;
          }
          .map-modal-content .leaflet-tile-container img {
            background: transparent !important;
          }
          .map-modal-content .leaflet-pane {
            z-index: 400 !important;
          }
          .map-modal-content .leaflet-map-pane {
            background: #fff !important;
          }
          .map-modal-content .leaflet-tile-pane {
            background: transparent !important;
          }
        `}</style>
        <div className="map-modal-overlay" onClick={(e) => {
          if (e.target.classList.contains('map-modal-overlay')) {
            setShowMapModal(false);
          }
        }}>
          <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-semibold text-gray-800">
                แผนที่เช็คอิน - {mapType === "arrive" ? "เวลาเข้า" : "เวลาออก"}
              </h3>
              <button
                onClick={() => setShowMapModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <Icon icon="mdi:close" className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 bg-white" style={{ backgroundColor: "white" }}>
              {data.location.lat && data.location.lng && (
                <div className="bg-white relative" style={{ zIndex: 1, backgroundColor: "white" }}>
                  <div style={{ backgroundColor: "white", width: "100%", height: "500px" }}>
                    <MapContainer
                      center={[data.location.lat, data.location.lng]}
                      zoom={16}
                      style={{ height: "500px", width: "100%", backgroundColor: "#fff" }}
                    >
                      <TileLayer url="https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}" />

                      {/* Marker สถานที่บริการ */}
                      <Marker position={[data.location.lat, data.location.lng]}>
                        <Popup>สถานที่บริการ</Popup>
                      </Marker>

                      {/* CircleMarker สถานที่บริการ */}
                      <CircleMarker
                        center={[data.location.lat, data.location.lng]}
                        radius={10}
                        pathOptions={{
                          color: "#2563eb",
                          fillColor: "#2563eb",
                          fillOpacity: 0.9,
                        }}
                      />

                      {/* ถ้ามีจุด check-in/out แสดง (ตาม mapType) */}
                      {activeCheckLocation.lat && activeCheckLocation.lng && (
                        <>
                          <CircleMarker
                            center={[activeCheckLocation.lat, activeCheckLocation.lng]}
                            radius={10}
                            pathOptions={{
                              color: mapType === "arrive" ? "#16a34a" : "#dc2626",
                              fillColor: mapType === "arrive" ? "#16a34a" : "#dc2626",
                              fillOpacity: 0.9,
                            }}
                          />
                          <Polyline
                            positions={[
                              [data.location.lat, data.location.lng],
                              [activeCheckLocation.lat, activeCheckLocation.lng],
                            ]}
                            pathOptions={{ color: "#2563eb", weight: 3 }}
                          />
                        </>
                      )}
                    </MapContainer>
                  </div>
                </div>
              )}

              {/* แสดงข้อมูลระยะทาง */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">ตำแหน่งสถานที่บริการ</div>
                    <div className="text-sm font-medium text-gray-800">
                      {data.location.lat?.toFixed(6)}, {data.location.lng?.toFixed(6)}
                    </div>
                  </div>
                  {activeCheckLocation.lat && activeCheckLocation.lng ? (
                    <>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">
                          จุดที่ช่าง {mapType === "arrive" ? "check-in (เข้า)" : "check-out (ออก)"}
                        </div>
                        <div className="text-sm font-medium text-gray-800">
                          {activeCheckLocation.lat?.toFixed(6)}, {activeCheckLocation.lng?.toFixed(6)}
                        </div>
                      </div>
                      {distance && (
                        <div className="col-span-2 mt-2 pt-2 border-t">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">ระยะทางห่างกัน:</span>
                            <span className={`text-lg font-bold ${
                              parseFloat(distance) > 1 ? "text-red-600" : "text-green-600"
                            }`}>
                              {distance} กม.
                            </span>
                          </div>
                          {parseFloat(distance) > 1 && (
                            <div className="text-xs text-red-600 mt-1">
                              ⚠️ อยู่นอกระยะทำการ (เกิน 1 กม.)
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="col-span-2 text-sm text-gray-500">
                      ยังไม่มีข้อมูลจุด {mapType === "arrive" ? "check-in" : "check-out"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex">
      <style>{`
        /* ลบ CSS ที่ override .leaflet-container ออก เพราะใช้จาก index.css แทน */
        /* แก้ไขเฉพาะแผนที่ย่อ - ไม่ให้เป็นสีชมพู */
        div.w-full.h-20 .leaflet-container,
        div.w-full.h-24 .leaflet-container,
        div.h-20 .leaflet-container,
        div.h-24 .leaflet-container {
          background: #ffffff !important;
          background-color: #ffffff !important;
        }
        div.w-full.h-20 .leaflet-map-pane,
        div.w-full.h-24 .leaflet-map-pane,
        div.h-20 .leaflet-map-pane,
        div.h-24 .leaflet-map-pane {
          background: #ffffff !important;
          background-color: #ffffff !important;
        }
        div.w-full.h-20 .leaflet-tile-pane,
        div.w-full.h-24 .leaflet-tile-pane,
        div.h-20 .leaflet-tile-pane,
        div.h-24 .leaflet-tile-pane {
          background: transparent !important;
          background-color: transparent !important;
        }
        /* Fix iframe map display - ใช้ specificity สูงมากที่สุด */
        div.w-full.h-20.rounded-lg.border-2.bg-white iframe,
        div.w-full.h-24.rounded-lg.border-2.bg-white iframe,
        div.w-full.h-20.rounded-lg.border-2[style*="background"] iframe,
        div.w-full.h-24.rounded-lg.border-2[style*="background"] iframe,
        div.w-full.h-20.rounded-lg.border-2 iframe,
        div.w-full.h-24.rounded-lg.border-2 iframe,
        iframe[title*="map"],
        iframe[title*="แผนที่"],
        iframe[src*="google.com/maps"],
        iframe[src*="maps"] {
          background: #ffffff !important;
          background-color: #ffffff !important;
          background-image: none !important;
          border: none !important;
          display: block !important;
          opacity: 1 !important;
          filter: none !important;
          mix-blend-mode: normal !important;
          visibility: visible !important;
          z-index: 1 !important;
        }
        /* Prevent parent background from showing through - ใช้ specificity สูงมาก */
        div.w-full.h-20.rounded-lg.border-2.bg-white,
        div.w-full.h-24.rounded-lg.border-2.bg-white,
        div.w-full.h-20.rounded-lg.border-2[style*="background"],
        div.w-full.h-24.rounded-lg.border-2[style*="background"],
        div.w-full.h-20.rounded-lg.border-2,
        div.w-full.h-24.rounded-lg.border-2,
        div[class*="rounded-lg"][class*="border"] iframe,
        div[class*="h-20"] iframe,
        div[class*="h-24"] iframe {
          background: #ffffff !important;
          background-color: #ffffff !important;
          background-image: none !important;
          filter: none !important;
          mix-blend-mode: normal !important;
        }
        /* แก้ไข wrapper div ที่มี bg-white */
        div.w-full.h-full.bg-white[style*="background"],
        div.w-full.h-full.bg-white {
          background: #ffffff !important;
          background-color: #ffffff !important;
          background-image: none !important;
        }
        /* แก้ไข div ที่มี iframe - ไม่ให้มี background สีชมพู */
        div.w-full.h-20.rounded-lg.border-2.bg-white:has(iframe),
        div.w-full.h-24.rounded-lg.border-2.bg-white:has(iframe),
        div.w-full.h-20.rounded-lg.border-2:has(iframe),
        div.w-full.h-24.rounded-lg.border-2:has(iframe),
        div[class*="rounded-lg"][class*="border"]:has(iframe),
        div[class*="h-20"]:has(iframe),
        div[class*="h-24"]:has(iframe) {
          background: #ffffff !important;
          background-color: #ffffff !important;
          background-image: none !important;
          filter: none !important;
          mix-blend-mode: normal !important;
        }
        /* Override any pink/red background - แต่ไม่ให้ override div ที่มี iframe */
        div[class*="rounded-lg"][class*="border"]:not(:has(iframe)):not([style*="backgroundImage"]):not([style*="background-image"]) {
          background: transparent !important;
          background-color: transparent !important;
        }
        /* ให้แผนที่แบบ background-image แสดงได้ */
        div[style*="backgroundImage"],
        div[style*="background-image"] {
          background-size: cover !important;
          background-position: center !important;
          background-repeat: no-repeat !important;
          background-color: #e5e7eb !important;
        }
        /* แก้ไขเฉพาะ div ที่มี class h-20 หรือ h-24 และมี background-image */
        div.h-20[style*="backgroundImage"],
        div.h-20[style*="background-image"],
        div.h-24[style*="backgroundImage"],
        div.h-24[style*="background-image"] {
          background-size: cover !important;
          background-position: center !important;
          background-repeat: no-repeat !important;
          background-color: #e5e7eb !important;
        }
      `}</style>
      <Sidebar />
      <div className="flex-1 min-h-screen bg-gray-50 flex flex-col" style={{ paddingLeft: "var(--sidebar-width)" }}>
        <Topbar
          breadcrumbs={[
            { label: "ปฏิทินงานช่าง", link: "/job/calendar" },
            { label: `งาน ${data.id}` },
            { label: "ปิดบริการ" }
          ]}
        />

        <div className="flex-1 flex flex-col min-h-0">
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-gray-50 px-8 pt-6 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <h1 className="text-xl font-bold text-gray-900">
                {data.title} ({data.team})
              </h1>
              <span className={`px-3 py-1 rounded-full text-xs ${
                isJobClosed
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}>
                {data.status}
              </span>
            </div>

            {/* Summary + Map + Photos */}
            <div className="grid grid-cols-12 gap-4">
            {/* Card 1: ข้อมูลลูกค้า (col-span-3) */}
            <div className="col-span-3 bg-white rounded-lg border p-4 space-y-3">
              <div className="text-[14px] font-semibold text-gray-700">ข้อมูลลูกค้า</div>
              <div className="text-[14px] text-gray-800">
                <span className="text-[14px] text-gray-500">เลขที่สัญญา : </span>
                {data.customer.contractId ? (
                  <Link
                    to={`/contract/view/${data.customer.contractId}`}
                    state={{ from: { label: "ปิดบริการ", path: `/job/close/${id}` }, jobId: data.id }}
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {data.customer.contractNo}
                  </Link>
                ) : (
                  <span className="font-medium">{data.customer.contractNo}</span>
                )}
              </div>
              <div className="text-[14px] text-gray-800">
                <span className="text-[14px] text-gray-500">ชื่อในสัญญา : </span>
                <span className="font-medium">{data.customer.name}</span>
              </div>
              <div className="text-[14px] text-gray-800">
                <span className="text-[14px] text-gray-500">ผู้ติดต่อ : </span>
                <span className="font-medium">{data.customer.contactName}</span>
              </div>
              <div className="text-[14px] text-gray-800">
                <span className="text-[14px] text-gray-500">เบอร์โทร : </span>
                <span className="font-medium">{data.customer.contactTel}</span>
              </div>
              <div className="text-[14px] text-gray-800">
                <span className="text-[14px] text-gray-500">ใบเสนอราคา : </span>
                {data.customer.quoteId ? (
                  <Link
                    to={`/quote/edit/${data.customer.quoteId}`}
                    state={{ from: { label: "ปิดบริการ", path: `/job/close/${id}` }, jobId: data.id }}
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {data.customer.quoteNo}
                  </Link>
                ) : (
                  <span className="font-medium">{data.customer.quoteNo}</span>
                )}
              </div>
            </div>

            {/* Card 2: ข้อมูลสถานที่บริการ + Checkin (col-span-3) */}
            <div className="col-span-3 bg-white rounded-lg border p-4 space-y-3">
              <div className="text-[14px] font-semibold text-gray-700">ข้อมูลสถานที่บริการ</div>
              <div className="text-[14px] text-gray-800">
                <span className="text-[14px] text-gray-500">ที่อยู่บริการ : </span>
                <span>{data.customer.address}</span>
              </div>
              
              {/* Checkin แผนที่ด้านล่าง แบ่งซ้าย-ขวา */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                {/* ซ้าย: เข้า (check-in) */}
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <div className="text-xs font-semibold text-gray-700">เข้า</div>
                    <div className="text-xs text-gray-600 whitespace-nowrap">{data.schedule.arrive || "-"}{checkInTimestamp ? ` / ${checkInTimestamp}` : ""}</div>
                  </div>
                  {(data.location?.lat && data.location?.lng) || (checkInLocation.lat && checkInLocation.lng) ? (
                    <div 
                      className="w-full h-16 rounded-lg border-2 border-gray-300 overflow-hidden bg-white map-mini-container cursor-pointer hover:border-blue-500 transition-colors"
                      onClick={() => {
                        setMapType("arrive");
                        setShowMapModal(true);
                      }}
                      title="คลิกเพื่อดูแผนที่ขนาดใหญ่"
                    >
                      <div className="w-full h-full map-mini-wrapper" style={{ position: 'relative', backgroundColor: '#ffffff' }}>
                        <MapContainer
                          center={checkInLocation.lat && checkInLocation.lng 
                            ? [checkInLocation.lat, checkInLocation.lng] 
                            : [data.location.lat, data.location.lng]}
                          zoom={17}
                          className="map-mini"
                          style={{ height: "100%", width: "100%", zIndex: 1, backgroundColor: '#ffffff', background: '#ffffff' }}
                          zoomControl={false}
                          dragging={false}
                          touchZoom={false}
                          doubleClickZoom={false}
                          scrollWheelZoom={false}
                          boxZoom={false}
                          keyboard={false}
                        >
                          <TileLayer 
                            url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                            attribution=""
                          />
                          {data.location?.lat && data.location?.lng && (
                            <CircleMarker
                              center={[data.location.lat, data.location.lng]}
                              radius={5}
                              pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.8, weight: 2 }}
                            />
                          )}
                          {checkInLocation.lat && checkInLocation.lng && (
                            <CircleMarker
                              center={[checkInLocation.lat, checkInLocation.lng]}
                              radius={5}
                              pathOptions={{ color: "#16a34a", fillColor: "#16a34a", fillOpacity: 0.9, weight: 2 }}
                            />
                          )}
                        </MapContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-16 bg-gray-100 flex items-center justify-center text-gray-400 rounded-lg border text-xs">
                      ไม่มีข้อมูล
                    </div>
                  )}
                </div>

                {/* ขวา: ออก (check-out) */}
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <div className="text-xs font-semibold text-gray-700">ออก</div>
                    <div className="text-xs text-gray-600 whitespace-nowrap">{data.schedule.leave || "-"}{checkOutTimestamp ? ` / ${checkOutTimestamp}` : ""}</div>
                  </div>
                  {(data.location?.lat && data.location?.lng) || (checkOutLocation.lat && checkOutLocation.lng) ? (
                    <div 
                      className="w-full h-16 rounded-lg border-2 border-gray-300 overflow-hidden bg-white map-mini-container cursor-pointer hover:border-blue-500 transition-colors"
                      onClick={() => {
                        setMapType("leave");
                        setShowMapModal(true);
                      }}
                      title="คลิกเพื่อดูแผนที่ขนาดใหญ่"
                    >
                      <div className="w-full h-full map-mini-wrapper" style={{ position: 'relative', backgroundColor: '#ffffff' }}>
                        <MapContainer
                          center={checkOutLocation.lat && checkOutLocation.lng 
                            ? [checkOutLocation.lat, checkOutLocation.lng] 
                            : [data.location.lat, data.location.lng]}
                          zoom={17}
                          className="map-mini"
                          style={{ height: "100%", width: "100%", zIndex: 1, backgroundColor: '#ffffff', background: '#ffffff' }}
                          zoomControl={false}
                          dragging={false}
                          touchZoom={false}
                          doubleClickZoom={false}
                          scrollWheelZoom={false}
                          boxZoom={false}
                          keyboard={false}
                        >
                          <TileLayer 
                            url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                            attribution=""
                          />
                          {data.location?.lat && data.location?.lng && (
                            <CircleMarker
                              center={[data.location.lat, data.location.lng]}
                              radius={5}
                              pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.8, weight: 2 }}
                            />
                          )}
                          {checkOutLocation.lat && checkOutLocation.lng && (
                            <CircleMarker
                              center={[checkOutLocation.lat, checkOutLocation.lng]}
                              radius={5}
                              pathOptions={{ color: "#dc2626", fillColor: "#dc2626", fillOpacity: 0.9, weight: 2 }}
                            />
                          )}
                        </MapContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-16 bg-gray-100 flex items-center justify-center text-gray-400 rounded-lg border text-xs">
                      ไม่มีข้อมูล
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Card 3: ลายเซ็นลูกค้า (col-span-3) */}
            <div className="col-span-3 bg-white rounded-lg border p-4 space-y-3">
              {data.teamName && data.teamName !== "-" && (
                <div className="text-[14px] text-gray-800">
                  <span className="text-[14px] text-gray-500">ทีม : </span>
                  <span className="font-medium">{data.teamName}</span>
                </div>
              )}
              <div className="text-[14px] text-gray-800">
                <span className="text-[14px] text-gray-500">วันที่เข้าบริการ : </span>
                <span className="font-medium">
                  {data.schedule.date && data.schedule.date !== "-" 
                    ? formatDateMonthYear(data.schedule.date) || data.schedule.date
                    : data.schedule.date}
                  {data.schedule.arrive && data.schedule.arrive !== "-" && data.schedule.leave && data.schedule.leave !== "-" 
                    ? ` ${data.schedule.arrive} - ${data.schedule.leave}`
                    : data.schedule.arrive && data.schedule.arrive !== "-"
                    ? ` ${data.schedule.arrive}`
                    : ""}
                </span>
              </div>
              <div className="text-[14px] font-semibold text-gray-700">ลายเซ็นลูกค้า</div>
              <div className="text-[14px] text-gray-800">
                {data.customerSignature?.signed ? (
                  <span className="text-green-600 font-medium">เซ็นแล้ว</span>
                ) : (
                  <span className="text-red-600 font-medium">ยังไม่เซ็น</span>
                )}
                {data.customerSignature?.signedBy && (
                  <span className="text-gray-500 text-xs ml-2">
                    {data.customerSignature.signedBy}
                    {data.customerSignature.signedDate && ` · ${moment(data.customerSignature.signedDate).format("DD/MM/YY HH:mm")}`}
                  </span>
                )}
              </div>
              {data.customerSignature?.signatureImage ? (
                <div className="mt-2 border-2 border-gray-200 rounded-lg p-2 bg-gray-50">
                  <img 
                    src={data.customerSignature.signatureImage} 
                    alt="ลายเซ็นลูกค้า" 
                    className="w-full h-auto max-h-64 object-contain mx-auto"
                  />
                </div>
              ) : data.customerSignature?.reason ? (
                <div className="mt-2 text-[12px] text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {data.customerSignature.reason}
                </div>
              ) : null}
            </div>

            {/* Card 4: ก่อนเข้าบริการ / หลังบริการ (col-span-3) */}
            <div className="col-span-3 bg-white rounded-lg border p-4 space-y-4">
              <div className="font-semibold">ก่อนเข้าบริการ / หลังบริการ</div>
              
              {/* ก่อนเข้าบริการ (รูปจาก check-in Mobile) */}
              <div className="space-y-2">
                <div className="text-sm text-gray-700 font-semibold">ก่อนเข้าบริการ</div>
                <div className="flex items-center gap-1 flex-wrap">
                  {data.photosBefore?.length > 0 ? (
                    <>
                      {data.photosBefore.slice(0, 5).map((src, idx) => (
                        <img
                          key={`b-${idx}`}
                          src={src}
                          alt={`ก่อนเข้าบริการ ${idx + 1}`}
                          className="w-10 h-10 object-cover rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setSelectedServiceImages(data.photosBefore);
                            setImageModalTitle("รูปภาพก่อนเข้าบริการ");
                            setShowImageModal(true);
                          }}
                        />
                      ))}
                      <button
                        onClick={() => {
                          setSelectedServiceImages(data.photosBefore);
                          setImageModalTitle("รูปภาพก่อนเข้าบริการ");
                          setShowImageModal(true);
                        }}
                        className="p-2 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                        title="ดูรูปภาพก่อนเข้าบริการ"
                      >
                        <Icon icon="mdi:image-outline" className="w-5 h-5" />
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-500">ยังไม่มีรูปจาก check-in</span>
                  )}
                </div>
              </div>

              {/* หลังบริการ (รูปจาก job-close Mobile) */}
              <div className="space-y-2">
                <div className="text-sm text-gray-700 font-semibold">หลังบริการ</div>
                <div className="flex items-center gap-1 flex-wrap">
                  {data.photosAfter?.length > 0 ? (
                    <>
                      {data.photosAfter.slice(0, 5).map((src, idx) => (
                        <img
                          key={`a-${idx}`}
                          src={src}
                          alt={`หลังบริการ ${idx + 1}`}
                          className="w-10 h-10 object-cover rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setSelectedServiceImages(data.photosAfter);
                            setImageModalTitle("รูปภาพหลังบริการ");
                            setShowImageModal(true);
                          }}
                        />
                      ))}
                      <button
                        onClick={() => {
                          setSelectedServiceImages(data.photosAfter);
                          setImageModalTitle("รูปภาพหลังบริการ");
                          setShowImageModal(true);
                        }}
                        className="p-2 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                        title="ดูรูปภาพหลังบริการ"
                      >
                        <Icon icon="mdi:image-outline" className="w-5 h-5" />
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-500">ยังไม่มีรูปจากปิดงาน</span>
                  )}
                </div>
              </div>
            </div>

          </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto px-8 py-6 min-h-0">
            {/* Service table (grouped by service, JobWorkTable style) */}
            <div className="space-y-4">
            {data.services.map((srv, sIdx) => (
              <div key={srv.id} className="bg-white rounded-xl shadow-md border border-gray-300 text-[14px]">
                <div className="px-4 py-3 bg-gray-50 border-b">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-gray-800">{sIdx + 1}.</span>
                      <span className="text-[#E13F57] font-semibold">{srv.title}</span>
                      <span className="px-2 py-1 rounded-full bg-green-100 text-green-700">
                        {srv.type}
                      </span>
                      {srv.notDoneToday && (
                        <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">
                          ยังไม่ทำวันนี้
                        </span>
                      )}
                      {!isJobClosed && (
                        <button
                          className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex-shrink-0"
                          onClick={() => {
                            setEditingMemoServiceId(srv.id);
                          }}
                          title="แก้ไข Memo (กดปุ่มบันทึกเพื่อบันทึกลงระบบ)"
                        >
                          <Icon icon="mdi:book-edit-outline" className="w-5 h-5" />
                        </button>
                      )}
                      {/* Memo Section - กดปุ่มแล้วแก้ไขในช่องเดิม */}
                      {editingMemoServiceId === srv.id ? (
                        <input
                          type="text"
                          value={serviceMemos[srv.id] || ""}
                          onChange={(e) =>
                            setServiceMemos((prev) => ({
                              ...prev,
                              [srv.id]: e.target.value,
                            }))
                          }
                          onBlur={() => setEditingMemoServiceId(null)}
                          autoFocus
                          className="h-9 w-[640px] max-w-[80vw] bg-white border border-blue-300 rounded-lg px-3 py-1.5 text-gray-800 leading-5 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                      ) : (
                        serviceMemos[srv.id] && (
                          <div className="max-w-[50%] bg-gray-50 border border-gray-300 rounded-lg px-3 py-1.5 text-gray-800 whitespace-pre-wrap break-words">
                            {serviceMemos[srv.id]}
                          </div>
                        )
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* แสดงรูปเล็กๆ 1-4 รูปทางซ้ายของ icon */}
                      {serviceImages[srv.id] && serviceImages[srv.id].length > 0 && (
                        <div className="flex items-center gap-1">
                          {serviceImages[srv.id].slice(0, 4).map((imgUrl, imgIdx) => (
                            <img
                              key={imgIdx}
                              src={imgUrl}
                              alt={`Service image ${imgIdx + 1}`}
                              className="w-10 h-10 object-cover rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setSelectedServiceImages(serviceImages[srv.id]);
                            setImageModalTitle("บันทึกการบริการ");
                            setShowImageModal(true);
                          }}
                            />
                          ))}
                        </div>
                      )}
                      <button
                        className="p-2 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                        onClick={() => {
                          if (serviceImages[srv.id] && serviceImages[srv.id].length > 0) {
                            setSelectedServiceImages(serviceImages[srv.id]);
                            setShowImageModal(true);
                          } else {
                            alert("ยังไม่มีรูปภาพ");
                          }
                        }}
                        title="ดูบันทึกการบริการ"
                      >
                        <Icon icon="mdi:image-outline" className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full border table-fixed">
                      <thead>
                        <tr className="bg-[#E13F57] text-white">
                          <th className="py-2 border px-1 w-10">เลือก</th>
                          <th className="py-2 border px-2 w-12">ลำดับ</th>
                          <th className="py-2 border px-2 text-left min-w-0">ขั้นตอนบริการ</th>
                          <th className="py-2 border px-2 text-left w-60">อุปกรณ์ / สารเคมี</th>
                          <th className="py-2 border px-2 text-center w-48 whitespace-nowrap">ปริมาณกำหนด</th>
                          <th className="py-2 border px-2 text-center w-48 whitespace-nowrap">ปริมาณใช้</th>
                          <th className="py-2 border px-2 text-left min-w-0">หมายเหตุ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {srv.steps.map((st, stepIdx) => {
                          const isEditing = editingServiceId === srv.id;
                          const edited = editedSteps[st.id] || {};
                          const plannedUnitDisp = st.unit && String(st.unit).trim() ? st.unit : "";
                          const usedUnitRaw = isEditing ? (edited.unit !== undefined ? edited.unit : st.unit) : st.unit;
                          const usedUnitDisp = usedUnitRaw && String(usedUnitRaw).trim() ? usedUnitRaw : "";
                          const rawItemId = isEditing
                            ? (edited.itemId !== undefined ? edited.itemId : st.itemId)
                            : st.itemId;
                          const idNum =
                            rawItemId != null && rawItemId !== ""
                              ? Number(rawItemId)
                              : null;
                          const effectiveItemId =
                            idNum != null && !Number.isNaN(idNum) && idNum > 0 ? idNum : null;
                          const displayItemName =
                            (isEditing
                              ? (edited.itemName !== undefined ? edited.itemName : st.itemName)
                              : st.itemName) || "";
                          const itemOptions =
                            effectiveItemId &&
                            !itemList.some((i) => Number(i.Item_Id) === effectiveItemId)
                              ? [
                                  {
                                    Item_Id: effectiveItemId,
                                    Item_Name: displayItemName || `สินค้า #${effectiveItemId}`,
                                    Item_Unit_ST: usedUnitRaw || "",
                                  },
                                  ...itemList,
                                ]
                              : itemList;
                          return (
                            <tr key={st.id} className="odd:bg-white even:bg-gray-50">
                              <td className="text-center border py-2 px-1 w-10">
                                <input 
                                  type="checkbox" 
                                  checked={isEditing ? (edited.checked !== undefined ? edited.checked : st.checked || false) : (st.checked || false)}
                                  onChange={(e) => isEditing && handleStepChange(st.id, "checked", e.target.checked)}
                                  disabled={!isEditing}
                                  className="accent-[#E13F57] cursor-pointer w-4 h-4" 
                                />
                              </td>
                              <td className="border px-2 text-center w-12">
                                {sIdx + 1}.{stepIdx + 1}
                              </td>
                              <td className="border px-2 min-w-0 break-words">{st.name}</td>
                              <td className="border px-2 w-60">
                                <select
                                  value={rawItemId != null && rawItemId !== "" ? String(rawItemId) : ""}
                                  onChange={(e) => {
                                    if (!isEditing) return;
                                    const selected = itemOptions.find((i) => String(i.Item_Id) === e.target.value);
                                    handleStepChange(st.id, "itemId", selected ? selected.Item_Id : null);
                                    handleStepChange(st.id, "itemCode", selected ? selected.Item_Code : "");
                                    handleStepChange(st.id, "itemName", selected ? selected.Item_Name : "");
                                    handleStepChange(st.id, "unit", selected ? (selected.Item_Unit_ST || "") : (edited.unit !== undefined ? edited.unit : st.unit));
                                  }}
                                  disabled={!isEditing}
                                  className={`w-full max-w-full min-w-0 border rounded px-2 py-1 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#E13F57] ${!isEditing ? "bg-gray-50 cursor-default" : "bg-white"}`}
                                >
                                  <option value="">-- เลือกสินค้า --</option>
                                  {itemOptions.map((i) => (
                                    <option key={i.Item_Id} value={String(i.Item_Id)}>{i.Item_Name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="relative min-h-[48px] border p-0 text-center w-48 whitespace-nowrap align-top">
                                <div className="absolute inset-0 flex flex-nowrap items-stretch justify-center gap-2 whitespace-nowrap px-2 py-1.5">
                                  <span className="flex w-24 shrink-0 items-center justify-center rounded border border-transparent px-2 text-center tabular-nums leading-none">
                                    {st.qty}
                                  </span>
                                  <span
                                    className="flex w-14 shrink-0 items-center justify-center overflow-hidden text-center text-ellipsis whitespace-nowrap text-gray-800"
                                    title={plannedUnitDisp || undefined}
                                  >
                                    {plannedUnitDisp || "\u00A0"}
                                  </span>
                                </div>
                              </td>
                              <td className="relative min-h-[48px] border p-0 text-center w-48 whitespace-nowrap align-top">
                                <div className="absolute inset-0 flex flex-nowrap items-stretch justify-center gap-2 whitespace-nowrap px-2 py-1.5">
                                  <input
                                    value={isEditing ? (edited.usedQty !== undefined ? edited.usedQty : st.usedQty ?? st.qty) : (st.usedQty ?? st.qty)}
                                    onChange={(e) => isEditing && handleStepChange(st.id, "usedQty", e.target.value)}
                                    readOnly={!isEditing}
                                    className={`box-border h-full min-h-0 w-24 shrink-0 self-stretch rounded border px-2 py-0 text-center text-base tabular-nums leading-none outline-none focus:ring-2 focus:ring-[#E13F57]/40 ${isEditing ? "bg-white" : "bg-gray-50"}`}
                                  />
                                  <span
                                    className="flex w-14 shrink-0 items-center justify-center overflow-hidden text-center text-ellipsis whitespace-nowrap text-gray-800"
                                    title={usedUnitDisp || undefined}
                                  >
                                    {usedUnitDisp || "\u00A0"}
                                  </span>
                                </div>
                              </td>
                              <td className="relative min-h-[48px] min-w-0 border p-0 align-top">
                                <div className="absolute inset-0 flex items-stretch gap-2 px-2 py-1.5">
                                  <input
                                    value={isEditing ? (edited.remark !== undefined ? edited.remark : st.remark || "") : (st.remark || "")}
                                    onChange={(e) => isEditing && handleStepChange(st.id, "remark", e.target.value)}
                                    readOnly={!isEditing}
                                    placeholder="หมายเหตุ"
                                    className={`box-border h-full min-h-0 min-w-0 flex-1 self-stretch rounded border px-2 py-0 text-base leading-snug outline-none focus:ring-2 focus:ring-[#E13F57]/40 ${isEditing ? "bg-white" : "bg-gray-50"}`}
                                  />
                                  {st.overuseReasonName && (
                                    <span className="flex min-w-0 flex-1 items-center self-stretch overflow-hidden text-ellipsis whitespace-nowrap rounded bg-amber-50 px-2 py-0 text-sm text-amber-700">
                                      เหตุผลที่ใช้เกิน: {st.overuseReasonName}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* ปุ่มแก้ไข/บันทึก/ยกเลิก - แสดงที่ขวาล่าง เฉพาะเมื่องานยังไม่ปิด */}
                  {!isJobClosed && (
                    <div className="flex justify-end mt-4 gap-2">
                      {editingServiceId === srv.id ? (
                        <>
                          <button
                            onClick={() => handleSaveService(srv.id)}
                            className="px-4 py-2 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-2 shadow-lg"
                          >
                            <Icon icon="mdi:content-save" className="w-5 h-5" />
                            บันทึก
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 shadow-lg"
                            title="ยกเลิกการแก้ไข"
                          >
                            <Icon icon="mdi:close" className="w-5 h-5" />
                          </button>
                        </>
                      ) : (
                        <button
                          className="p-2 rounded-full bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                          onClick={() => handleEditService(srv.id)}
                          title="แก้ไขข้อมูล"
                        >
                          <Icon icon="mdi:pencil-outline" className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            </div>
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-0 z-10 bg-gray-50 px-8 py-4 border-t border-gray-200">
            <div className="flex justify-end gap-3">
              <button
                className={`px-5 py-2 rounded-full text-white transition-colors flex items-center gap-2 ${
                  isGeneratingLink ? "bg-purple-400 cursor-not-allowed" : "bg-purple-600 hover:bg-purple-700"
                }`}
                onClick={async () => {
                  setIsGeneratingLink(true);
                  try {
                    const res = await api.post("/app/generate-feedback", {
                      calendar_id: Number(id),
                      doc_id: data?.customer?.contractId || null,
                    });
                    if (res.data?.status === "OK") {
                      const feedbackToken = String(res.data?.token || "").trim();
                      if (!feedbackToken) {
                        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบ token สำหรับสร้าง link ประเมิน", "error");
                        return;
                      }
                      const feedbackUrl = `${window.location.origin}/feedback/${encodeURIComponent(feedbackToken)}`;
                      Swal.fire({
                        title: "Link ประเมินความพึงพอใจ",
                        html: `
                          <div style="display:flex;align-items:center;gap:6px;margin-top:8px;">
                            <input id="swal-fb-input" readonly value="${feedbackUrl}"
                              style="flex:1;border:1px solid #d1d5db;border-radius:6px;padding:6px 10px;font-size:13px;outline:none;" />
                            <button id="swal-fb-copy" style="background:#E13F57;color:#fff;border:none;border-radius:6px;padding:6px 14px;cursor:pointer;font-size:13px;">คัดลอก</button>
                          </div>`,
                        didOpen: () => {
                          document.getElementById("swal-fb-copy").addEventListener("click", () => {
                            navigator.clipboard.writeText(feedbackUrl);
                            document.getElementById("swal-fb-copy").textContent = "คัดลอกแล้ว ✓";
                          });
                        },
                        confirmButtonText: "ปิด",
                      });
                    } else {
                      Swal.fire("เกิดข้อผิดพลาด", res.data?.message || "ไม่สามารถสร้าง link ได้", "error");
                    }
                  } catch (e) {
                    Swal.fire("เกิดข้อผิดพลาด", e.response?.data?.message || "ไม่สามารถสร้าง link ได้", "error");
                  }
                  setIsGeneratingLink(false);
                }}
                disabled={isGeneratingLink}
              >
                <Icon icon="mdi:star-circle" className="w-4 h-4" />
                {isGeneratingLink ? "กำลังสร้าง..." : "สรุปผลสถานะงาน"}
              </button>
              <button
                disabled={isJobClosed}
                className={`px-5 py-2 rounded-full text-white transition-colors ${
                  isJobClosed
                    ? "bg-gray-400 cursor-not-allowed opacity-60"
                    : "bg-green-600 hover:bg-green-700"
                }`}
                onClick={handleCloseJob}
              >
                {isJobClosed ? "ปิดงานแล้ว" : "ปิดงาน"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Map Modal */}
      {renderMapModal()}

      {/* Image Modal */}
      {lightboxSrc && (
        <div
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.85)", zIndex: 19999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => { setLightboxSrc(null); setLightboxIndex(null); }}
        >
          <button
            onClick={() => { setLightboxSrc(null); setLightboxIndex(null); }}
            style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 32, lineHeight: 1 }}
          >×</button>
          {/* Prev button */}
          {selectedServiceImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIdx = (lightboxIndex - 1 + selectedServiceImages.length) % selectedServiceImages.length;
                setLightboxIndex(newIdx);
                setLightboxSrc(selectedServiceImages[newIdx]);
              }}
              style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 36, lineHeight: 1, borderRadius: 8, padding: "8px 14px" }}
            >‹</button>
          )}
          <img
            src={lightboxSrc}
            alt="ดูรูปภาพ"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}
          />
          {/* Next button */}
          {selectedServiceImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIdx = (lightboxIndex + 1) % selectedServiceImages.length;
                setLightboxIndex(newIdx);
                setLightboxSrc(selectedServiceImages[newIdx]);
              }}
              style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 36, lineHeight: 1, borderRadius: 8, padding: "8px 14px" }}
            >›</button>
          )}
          {/* Counter */}
          {selectedServiceImages.length > 1 && lightboxIndex !== null && (
            <div style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", color: "#fff", fontSize: 14, background: "rgba(0,0,0,0.5)", borderRadius: 20, padding: "4px 16px" }}>
              {lightboxIndex + 1} / {selectedServiceImages.length}
            </div>
          )}
        </div>
      )}

      {showImageModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={() => setShowImageModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              width: '100%',
              maxWidth: '56rem',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              zIndex: 10000
            }}
            onClick={(e) => e.stopPropagation()}
          >
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
                <h3 className="text-lg font-semibold text-gray-800">
                  {imageModalTitle} ({selectedServiceImages.length} รูป)
                </h3>
                <button
                  onClick={() => setShowImageModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Icon icon="mdi:close" className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4 bg-white">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {selectedServiceImages.map((imgUrl, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={imgUrl}
                        alt={`Service image ${idx + 1}`}
                        className="w-full h-48 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => { setLightboxSrc(imgUrl); setLightboxIndex(idx); }}
                      />
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                        {idx + 1}/{selectedServiceImages.length}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
      )}
    </div>
  );
}